import { HuonekorttiRequestPut } from "@/api/models/huonekortti-request-put";
import { HuonekorttiResponse } from "@/api/models/huonekortti-response";
import { ElMessage, ElMessageBox } from "element-plus";
import _ from "lodash";
import { ref, watch } from "vue";
import { useHakuComponent } from "../haku";
import { useHuonekorttiSelection } from "../huonekorttiSelection";
import { useHuonekortitUpdate } from "@/methods/huonekorttiComposablses";

const huonekortit = ref<HuonekorttiResponse[]>([])
const huonekortitRequest = ref<{ id: number, request: HuonekorttiRequestPut }[]>([])
const loading = ref(false)
const updateSuccess = ref<number[]>([])
const updateErrors = ref<number[]>([])
const saveInProgress = ref(false)

export function useHuonekorttiMuokkausComponent() {
    const editedHuonekortti = ref<HuonekorttiResponse>()
    const editedId = ref<number>()
    const {
        responses: updateResponses,
        errors: instanceUpdateErrors,
        loading: updateLoading,
        updateHuonekortit
    } = useHuonekortitUpdate()
    const { searchLoading, hae } = useHakuComponent()
    const { huonekorttiEditModalVisible, selectedHuonekortit } = useHuonekorttiSelection()

    const muokkaaHuonekorttia = (huonekortti: HuonekorttiResponse) => {
        editedHuonekortti.value = _.cloneDeep(huonekortti)
        editedId.value = huonekortti.id
    }
    const peruutaMuokkaus = () => {
        editedHuonekortti.value = undefined
        editedId.value = undefined
    }
    const tallennaMuokkaus = () => {
        const huonekortti = huonekortit.value.find(h => h.id == editedId.value)

        if (huonekortti && editedHuonekortti.value) {
            huonekortti.henkilomaaraNorm = editedHuonekortti.value.henkilomaaraNorm
            huonekortti.henkilomaaraMax = editedHuonekortti.value.henkilomaaraMax
            huonekortti.toimintaAika = editedHuonekortti.value.toimintaAika
            huonekortti.sahkoluokka = editedHuonekortti.value.sahkoluokka
        }

        peruutaMuokkaus()
    }
    const overwriteHuonekortit = (huonekortti: HuonekorttiResponse) => {
        huonekortit.value.forEach(h => {
            if (huonekortti.henkilomaaraNorm != null) {
                h.henkilomaaraNorm = huonekortti.henkilomaaraNorm
            }
            if (huonekortti.henkilomaaraMax != null) {
                h.henkilomaaraMax = huonekortti.henkilomaaraMax
            }
            if (huonekortti.toimintaAika != null) {
                h.toimintaAika = huonekortti.toimintaAika
            }
            if (huonekortti.sahkoluokka != null) {
                h.sahkoluokka = huonekortti.sahkoluokka
            }
        })
    }
    const tallennaHuonekortit = () => {
        ElMessageBox.confirm("Haluatko varmasti tallentaa muutokset?", "Vahvista", {
            confirmButtonText: "Kyllä",
            cancelButtonText: "Peruuta",
            type: "warning"
        }).then(() => {
            loading.value = true
            saveInProgress.value = true

            huonekortitRequest.value = huonekortit.value.map(h => {
                const request: HuonekorttiRequestPut = {
                    toiminta: h.toiminta,
                    toiminnanKuvaus: h.toiminnanKuvaus,
                    toiminnanVaatimukset: h.toiminnanVaatimukset,
                    henkilomaaraNorm: h.henkilomaaraNorm,
                    henkilomaaraMax: h.henkilomaaraMax,
                    toimintaAika: h.toimintaAika,
                    sahkoluokka: h.sahkoluokka,
                    tilanimike: h.tilanimike ? { id: h.tilanimike.id } : undefined,
                    vastuuyksikko: h.vastuuyksikko ? { id: h.vastuuyksikko.id as number } : undefined,
                    tilat: h.tilat?.map(t => ({
                        id: t.id,
                        tilatunnus: t.tilatunnus,
                        pintaAla: t.pintaAla,
                        vastuuyksikko: t.vastuuyksikko ? { id: t.vastuuyksikko.id as number } : undefined,
                        huonekortti: t.huonekortti ? { id: t.huonekortti.id } : undefined,
                    })),
                }

                return { id: h.id, request }
            })

            updateHuonekortit(huonekortitRequest.value)
        }).catch(() => {
            loading.value = false
        })
    }
    const resetState = () => {
        huonekortit.value = []
        huonekortitRequest.value = []
        updateSuccess.value = []
        updateErrors.value = []
        loading.value = false
        saveInProgress.value = false
    }

    watch(updateResponses, (value) => {
        if (value.length > 0) {
            updateSuccess.value = value.map(r => r.id)
        }
    }, { deep: true })
    watch(instanceUpdateErrors, (value) => {
        updateErrors.value = value
    }, { deep: true })
    watch(updateLoading, (value) => {
        if (!saveInProgress.value) {
            return
        }
        if (updateErrors.value.length > 0) {
            loading.value = false
            saveInProgress.value = false
            ElMessage.error("Vähintään yhden huonekortin päivitys epäonnistui")

            return
        }
        if (!value) {
            hae()
        }
    }, { deep: true })
    watch(searchLoading, (value) => {
        if (saveInProgress.value && huonekortit.value.length > 0 && !value && updateErrors.value.length === 0) {
            loading.value = false
            saveInProgress.value = false
            ElMessage.success("Huonekortit päivitetty")

            setTimeout(() => {
                huonekorttiEditModalVisible.value = false
                selectedHuonekortit.value = []
            }, 2000)
        }
    })

    return {
        huonekortit,
        loading,
        updateSuccess,
        updateErrors,
        editedHuonekortti,
        editedId,
        muokkaaHuonekorttia,
        peruutaMuokkaus,
        tallennaMuokkaus,
        overwriteHuonekortit,
        tallennaHuonekortit,
        resetState,
    }
}
